import { merge } from 'es-toolkit/compat';
import { castAPIData } from 'shared/api/APISchema';
import { AddLoadDTO, addLoadSchema } from '../../data/LoadDTO';

export interface AddLoadLocationState {
  dispatchSheetFile?: File;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function omitNullishValues<T>(value: T): T {
  if (value === null || value === undefined) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => omitNullishValues(item)) as T;
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const result: Record<string, unknown> = {};

  Object.entries(value).forEach(([key, fieldValue]) => {
    if (fieldValue === null || fieldValue === undefined) {
      return;
    }

    result[key] = omitNullishValues(fieldValue);
  });

  return result as T;
}

function mergeParsedVehicles(
  baseVehicles: AddLoadDTO['vehicles'],
  parsedVehicles: AddLoadDTO['vehicles'],
): AddLoadDTO['vehicles'] {
  if (!parsedVehicles.length) {
    return baseVehicles;
  }

  return parsedVehicles.map((parsedVehicle, index) => ({
    ...(baseVehicles[index] ?? baseVehicles[0]),
    ...parsedVehicle,
  }));
}

export function mergeParsedDispatchSheetIntoAddLoadValues<T extends AddLoadDTO>(
  base: T,
  parsed: AddLoadDTO,
): T {
  const sanitizedParsed = omitNullishValues(parsed);

  const merged = castAPIData(addLoadSchema, {
    ...base,
    ...sanitizedParsed,
    pickup: merge({}, base.pickup, sanitizedParsed.pickup),
    delivery: merge({}, base.delivery, sanitizedParsed.delivery),
    customer: merge({}, base.customer, sanitizedParsed.customer),
    vehicles: mergeParsedVehicles(
      base.vehicles,
      sanitizedParsed.vehicles ?? [],
    ),
    // Add Load form supports a single payment slot; merge only payments[0].
    payments: sanitizedParsed.payments?.length
      ? [{ ...base.payments[0], ...sanitizedParsed.payments[0] }]
      : base.payments,
  }) as T;

  return {
    ...merged,
    vehicles: merged.vehicles.map((vehicle) => omitNullishValues(vehicle)),
  };
}
